import type { KnowledgeCatalog, KnowledgeSource } from '@kizen/api/knowledge';
import { TFunction } from 'react-i18next';

export const getPageTitle = (t: TFunction, subpage?: string) => {
  switch (subpage) {
    case 'online-sources':
      return t('Knowledge Library - Online Sources');
    default:
      return t('Knowledge Library - Files');
  }
};

export const getProcessingStatusesConfig = (
  t: TFunction
): Record<KnowledgeSource['processing_status'], Record<string, string>> => ({
  processed: {
    label: t('Complete'),
    color: 'var(--color-viz-cat-1-primary)',
  },
  failed: {
    label: t('Error'),
    color: 'var(--color-viz-cat-7-primary)',
  },
  pending: {
    label: t('In Progress'),
    color: 'var(--color-viz-cat-3-primary)',
  },
});

export const getOnlineSourcesStatusesConfig = (
  t: TFunction
): Record<'active' | 'inactive', Record<string, string>> => ({
  active: {
    label: t('Active'),
    color: 'var(--color-viz-cat-1-primary)',
  },
  inactive: {
    label: t('Inactive'),
    color: 'var(--color-viz-cat-7-primary)',
  },
});

export const getExecutionStatusConfig = (
  t: TFunction
): Record<string, Record<string, string>> => ({
  setup: {
    label: t('Setup'),
    color: 'var(--color-viz-cat-3-primary)',
  },
  active: {
    label: t('Active'),
    color: 'var(--color-viz-cat-1-primary)',
  },
  inactive: {
    label: t('Disabled'),
    color: 'var(--color-viz-cat-7-primary)',
  },
  running: {
    label: t('Running'),
    color: 'var(--color-viz-cat-3-primary)',
  },
  failed: {
    label: t('Failed'),
    color: 'var(--color-viz-cat-7-primary)',
  },
  completed: {
    label: t('Completed'),
    color: 'var(--color-viz-cat-1-primary)',
  },
  cancelled: {
    label: t('Cancelled'),
    color: 'var(--color-viz-cat-7-primary)',
  },
  cancel_pending: {
    label: t('Cancel Pending'),
    color: 'var(--color-viz-cat-3-primary)',
  },
  pending: {
    label: t('Pending'),
    color: 'var(--color-viz-cat-3-primary)',
  },
  finalizing: {
    label: t('Finalizing'),
    color: 'var(--color-viz-cat-3-primary)',
  },
});

export const getCatalogStatusConfig = (
  t: TFunction
): Record<'active' | 'disabled' | 'preview_only', Record<string, string>> => ({
  active: {
    label: t('Active'),
    color: 'var(--color-viz-cat-1-primary)',
  },
  disabled: {
    label: t('Disabled'),
    color: 'var(--color-viz-cat-7-primary)',
  },
  preview_only: {
    label: t('Preview Only'),
    color: 'var(--color-viz-cat-3-primary)',
  },
});

const getSharingStatCount = (stats: unknown, role: string): number => {
  if (stats === null || typeof stats !== 'object') return 0;
  const value = (stats as Record<string, unknown>)[role];
  return typeof value === 'number' ? value : 0;
};

export const getCatalogStatusKey = (
  catalog: KnowledgeCatalog
): 'active' | 'disabled' | 'preview_only' => {
  if (!catalog.is_enabled) return 'disabled';
  const adminCount = getSharingStatCount(catalog.sharing_stats, 'Admin');
  const editCount = getSharingStatCount(catalog.sharing_stats, 'Edit');
  const viewCount = getSharingStatCount(catalog.sharing_stats, 'View');
  if (adminCount === 0 && editCount === 0 && viewCount === 0)
    return 'preview_only';
  return 'active';
};

export const isPluginCatalog = (catalog?: KnowledgeCatalog | null): boolean => {
  const pluginApp = catalog?.plugin_app;
  return Array.isArray(pluginApp) && pluginApp.length > 0;
};

export const getPluginApiName = (catalog?: KnowledgeCatalog | null): string => {
  const app = catalog?.plugin_app?.[0] as { api_name?: string } | undefined;
  return app?.api_name ?? '';
};

export const getOnlineSourcePageStatusesConfig = (
  t: TFunction
): Record<
  'active' | 'disabled' | 'disabled_by_source',
  Record<string, string>
> => ({
  active: {
    label: t('Active'),
    color: 'var(--color-viz-cat-1-primary)',
  },
  disabled: {
    label: t('Disabled'),
    color: 'var(--color-viz-cat-7-primary)',
  },
  disabled_by_source: {
    label: t('Disabled by Source'),
    color: 'var(--color-viz-cat-7-primary)',
  },
});
